import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react';
import { knowledgeBaseService, type KBDocumentDetails } from '../services/knowledgeBaseService';
import { useToast } from './ToastProvider';
import './KBDocumentsTab.css';
import { useTranslation } from 'react-i18next';
import MarkdownViewer from './MarkdownViewer';
import DerivativesDropdown from './DerivativesDropdown';
import { useDocumentDerivatives } from '../hooks/useDocumentDerivatives';
import KbSearchPanel from './KbSearchPanel';

// A failed delete must stay visible until the user dismisses it (issue #347): the backend refuses
// the delete when the vector-store cleanup fails, and a 5 s auto-dismissed toast was missed.
const DELETE_ERROR_TTL_MS = 0;

function deleteFailureMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : fallback;
}

const RELEVANCE_CONTEXTS = [
  'state_of_the_art', 'our_solution', 'fto', 'patentability', 'industrialization', 'patent_application',
];

export interface KBDocumentsTabHandle {
  deleteSelected: () => Promise<string[]>;
  clearSelection: () => void;
  refresh: () => Promise<void>;
}

interface KBDocumentsTabProps {
  kbId?: string;
  kbName: string;
  documentIds?: string[]; // Currently unused
  collectionMode?: boolean; // Currently unused
  onDocumentSelect?: (documentId: string, derivativeType: string) => void; // Currently unused
  onSelectionChange?: (selectedIds: string[]) => void;
  onDocumentsDeleted?: (deletedIds: string[]) => void;
  docNumberMap?: Map<string, number>;
  onSearchTabCreated?: (transcriptId: string, label: string) => void;
  onSearchResultsReady?: (transcriptId: string, documents: import('./KbSearchPanel').KbSearchResultDocument[]) => void;
  onSearchError?: (transcriptId: string, message: string) => void;
}

const KBDocumentsTab = forwardRef<KBDocumentsTabHandle, KBDocumentsTabProps>(function KBDocumentsTab(
  { kbId, kbName, onSelectionChange, onDocumentsDeleted, docNumberMap, onSearchTabCreated, onSearchResultsReady, onSearchError }: KBDocumentsTabProps,
  ref
) {

  const { t } = useTranslation();
  const { addToast } = useToast();
  const [documents, setDocuments] = useState<KBDocumentDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchFirst, setShowSearchFirst] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [relevanceContext, setRelevanceContext] = useState('state_of_the_art');
  const {
    derivatives, availableFunctions, processingDoc, markdownViewer, setMarkdownViewer,
    loadDocumentDerivatives, hasDerivative, executeFunction, fetchJobs, cancelFetch,
  } = useDocumentDerivatives(kbId);

  const loadDocuments = useCallback(
    async (search?: string) => {
      if (!kbId) return;
      try {
        setLoading(true);

        const response = await knowledgeBaseService.getDocumentsWithDetails(kbId, 0, 100, search, relevanceContext);
        const total = response.total_count || 0;
        setTotalCount(total);

        const page = response.documents || [];
        const searching = !!(search && search.trim());
        const shown = !searching && total >= 100 ? page.slice(0, 50) : page;
        const sorted = searching
          ? shown
          : [...shown].sort((a, b) => {
              const ra = a.relevance == null || a.relevance === '' ? -1 : Number(a.relevance);
              const rb = b.relevance == null || b.relevance === '' ? -1 : Number(b.relevance);
              return rb - ra;
            });
        setDocuments(sorted);
        setShowSearchFirst(false);
      } catch (error) {
        console.error('Error loading documents:', error);
        addToast({
          type: 'error',
          title: t('kbDocuments.loadErrorTitle'),
          message: t('kbDocuments.loadErrorMessage'),
        });
      } finally {
        setLoading(false);
      }
    },
    [kbId, addToast, t, relevanceContext]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadDocuments(searchQuery);
  };

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    documents.forEach(doc => {
      if (!derivatives[doc.id]) {
        loadDocumentDerivatives(doc.id);
      }
    });
  }, [documents, derivatives, loadDocumentDerivatives]);

  // Sync selections when documents list changes (e.g., after search/filter)
  // Keep only selections that still exist in current document list
  useEffect(() => {
    setSelectedDocuments(prev => {
      const next = new Set<string>();
      documents.forEach(doc => {
        if (prev.has(doc.id)) {
          next.add(doc.id);
        }
      });
      return next;
    });
  }, [documents]);

  // Notify parent when selection changes (separate effect to avoid setState-during-render)
  useEffect(() => {
    const selectedArray = Array.from(selectedDocuments);
    onSelectionChange?.(selectedArray);
  }, [selectedDocuments, onSelectionChange]);

  const toggleSelection = (documentId: string) => {
    setSelectedDocuments(prev => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedDocuments(new Set());
  };

  const handleDeleteDocument = async (documentId: string, documentName: string) => {
    if (!kbId) return;

    const confirmed = window.confirm(
      t('kbDocuments.confirmDelete', { name: documentName })
    );

    if (!confirmed) return;

    try {
      await knowledgeBaseService.removeDocument(kbId, documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setTotalCount(prev => Math.max(0, prev - 1));
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
      onDocumentsDeleted?.([documentId]);
      addToast({
        type: 'success',
        title: t('kbDocuments.deletedTitle'),
        message: t('kbDocuments.documentDeleted', { name: documentName }),
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        title: t('kbDocuments.deleteFailedTitle'),
        message: deleteFailureMessage(error, t('kbDocuments.deleteFailedMessage', { id: documentId })),
        ttlMs: DELETE_ERROR_TTL_MS,
      });
    }
  };

  const deleteSelected = async (): Promise<string[]> => {
    if (!kbId) return [];
    const toDelete = Array.from(selectedDocuments);
    if (!toDelete.length) return [];

    const deleted: string[] = [];
    for (const docId of toDelete) {
      try {
        await knowledgeBaseService.removeDocument(kbId, docId);
        deleted.push(docId);
      } catch (error: unknown) {
        addToast({
          type: 'error',
          title: t('kbDocuments.deleteFailedTitle'),
          message: deleteFailureMessage(error, t('kbDocuments.deleteFailedMessage', { id: docId })),
          ttlMs: DELETE_ERROR_TTL_MS,
        });
      }
    }

    if (deleted.length > 0) {
      setDocuments(prev => prev.filter(doc => !deleted.includes(doc.id)));
      setTotalCount(prev => Math.max(0, prev - deleted.length));
      clearSelection();
      onDocumentsDeleted?.(deleted);
      addToast({
        type: 'success',
        title: t('kbDocuments.deletedTitle'),
        message: t('kbDocuments.deletedMessage', { count: deleted.length }),
      });
    }

    return deleted;
  };

  useImperativeHandle(ref, () => ({
    deleteSelected,
    clearSelection,
    refresh: () => loadDocuments(),
  }));

  const typeChar = (dt?: string) =>
    dt === 'patent' ? 'P' : dt === 'scientific_publication' ? 'S' : '·';
  const typeTitle = (dt?: string) =>
    dt === 'patent'
      ? t('kbDocuments.typePatent')
      : dt === 'scientific_publication'
        ? t('kbDocuments.typeSciPub')
        : (dt || '');
  const fmtRelevance = (r?: string | number) =>
    r == null || r === '' ? '' : String(Math.round(Number(r)));

  if (loading && !showSearchFirst) {
    return (
      <div className="kb-documents-tab">
        <div className="loading-spinner">{t('kbDocuments.loading')}</div>
      </div>
    );
  }

  return (
    <div className="kb-documents-tab">
      {markdownViewer && (
        <MarkdownViewer
          title={markdownViewer.title}
          content={markdownViewer.content}
          onClose={() => setMarkdownViewer(null)}
        />
      )}
      {kbId && onSearchTabCreated && onSearchResultsReady && onSearchError && (
        <KbSearchPanel
          kbId={kbId}
          kbName={kbName}
          onTabCreated={onSearchTabCreated}
          onResultsReady={onSearchResultsReady}
          onError={onSearchError}
        />
      )}
      <div className="search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('kbDocuments.searchPlaceholder')}
            className="search-input"
          />
          <button type="submit" className="search-btn">
            {t('common.search')}
          </button>
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                loadDocuments('');
              }}
              className="clear-btn"
            >
              {t('common.clear')}
            </button>
          )}
        </form>
      </div>

      <div className="documents-header">
        <h3>{t('kbDocuments.title', { kbName })}</h3>
        <div className="relevance-context">
          <label htmlFor="relevance-context-select">{t('kbDocuments.relevanceContext')}</label>
          <select
            id="relevance-context-select"
            value={relevanceContext}
            onChange={(e) => setRelevanceContext(e.target.value)}
          >
            {RELEVANCE_CONTEXTS.map((c) => (
              <option key={c} value={c}>{t(`kbDocuments.contexts.${c}`)}</option>
            ))}
          </select>
        </div>
        <div className="document-count">
          {totalCount > 50 && !searchQuery
            ? t('kbDocuments.totalLarge', { shown: documents.length, count: totalCount })
            : t('kbDocuments.total', { count: totalCount })}
        </div>
      </div>

      {documents.length === 0 && !loading ? (
        <div className="no-documents">
          {t('kbDocuments.noDocuments')}
        </div>
      ) : (
        <div className="documents-table">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{t('kbDocuments.table.type')}</th>
                <th>{t('kbDocuments.table.relevance')}</th>
                <th>{t('kbDocuments.table.reference')}</th>
                <th>{t('kbDocuments.table.title')}</th>
                <th>{t('kbDocuments.table.date')}</th>
                <th>{t('kbDocuments.table.person')}</th>
                <th>{t('kbDocuments.table.der')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td className="kb-doc-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.has(doc.id)}
                      onChange={() => {

                        toggleSelection(doc.id);
                      }}
                    />
                    {selectedDocuments.has(doc.id) && docNumberMap?.has(doc.id) && (
                      <span className="kb-doc-number">#{docNumberMap.get(doc.id)}</span>
                    )}
                  </td>
                  <td title={typeTitle(doc.document_type)}>{typeChar(doc.document_type)}</td>
                  <td className="kb-doc-relevance">{fmtRelevance(doc.relevance)}</td>
                  <td className="kb-doc-reference">{doc.reference}</td>
                  <td className="kb-doc-title" title={doc.title || doc.full_filename}>{doc.title || doc.full_filename}</td>
                  <td className="kb-doc-date">{doc.pub_month_year}</td>
                  <td className="kb-doc-person" title={doc.person}>{doc.person}</td>
                  <td>
                    <DerivativesDropdown
                      doc={doc}
                      functions={availableFunctions}
                      isProcessing={processingDoc === doc.id}
                      hasDerivative={hasDerivative}
                      onExecute={executeFunction}
                      placeholder={t('kbDocuments.viewsPlaceholder')}
                      className="functions-dropdown"
                      fetchJob={fetchJobs[doc.id] ?? null}
                      onCancelFetch={() => cancelFetch(doc.id)}
                    />
                  </td>
                  <td className="delete-column">
                    <button
                      type="button"
                      className="btn-delete-doc"
                      onClick={() => handleDeleteDocument(doc.id, doc.display_name)}
                      title={t('kbDocuments.deleteDocument')}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

export default KBDocumentsTab;
